import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useTenant } from "../app/TenantContext";
import { api } from "../api/client";
import { useApi } from "../hooks/useApi";

interface ContextBarProps {
  children?: ReactNode;
}

/**
 * Sticky Context-Bar mit Breadcrumb + Tenant-Strip.
 *
 * Beim Scroll > 80px schrumpft der Tenant-Strip und der Border erscheint
 * (per `.ctx-shrink`-Class) — visual cue, dass man scrollt. Die Logik
 * sitzt hier statt im AppShell, damit der Scroll-Effekt isoliert ist.
 */
export function ContextBar({ children }: ContextBarProps) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className={`ctx ${scrolled ? "ctx-shrink" : ""}`}>
      <div className="ctx-inner">
        <div className="ctx-trail">{children ?? <BreadCrumb />}</div>
        <TenantStrip />
      </div>
    </div>
  );
}

function BreadCrumb() {
  const location = useLocation();
  const params = useParams();
  const segs = breadcrumbSegments(location.pathname, params);

  return (
    <div className="crumb mono">
      {segs.map((seg, i) => (
        <span key={`${seg.label}-${i}`} className="crumb-seg">
          {i > 0 && <span className="crumb-sep">/</span>}
          {seg.to && i < segs.length - 1 ? (
            <Link to={seg.to} className="crumb-link">
              {seg.label}
            </Link>
          ) : (
            <span className="crumb-cur">{seg.label}</span>
          )}
        </span>
      ))}
      <span className="crumb-spacer" />
      <span className="crumb-status">
        <span className="status-dot" />
        <span className="uppercase-mini">backend online</span>
      </span>
    </div>
  );
}

interface Segment {
  label: string;
  to?: string;
}

function breadcrumbSegments(pathname: string, params: Record<string, string | undefined>): Segment[] {
  const root: Segment = { label: "Innendienst", to: "/anfragen" };

  if (pathname.startsWith("/anfragen/neu")) {
    return [root, { label: "Anfragen", to: "/anfragen" }, { label: "Neu" }];
  }
  if (pathname.startsWith("/anfragen/") && params.anfrageId) {
    return [
      root,
      { label: "Anfragen", to: "/anfragen" },
      { label: params.anfrageId },
    ];
  }
  if (pathname === "/anfragen") {
    return [root, { label: "Anfragen" }];
  }
  if (pathname.startsWith("/stammdaten/")) {
    const sub = pathname.split("/")[2] ?? "";
    const subLabel = {
      catalog: "Catalog",
      kunden: "Kunden",
      konditionen: "Konditionen",
      artikel: "Artikel (Legacy)",
      "tenant-config": "Tenant-Config",
    }[sub] ?? sub;
    return [root, { label: "Stammdaten", to: "/stammdaten" }, { label: subLabel }];
  }
  if (pathname === "/stammdaten") {
    return [root, { label: "Stammdaten" }];
  }
  if (pathname === "/health") {
    return [root, { label: "Health" }];
  }
  return [root];
}

function TenantStrip() {
  const { tenantId } = useTenant();
  const { state } = useApi(() => api.getStammdatenSummary(tenantId), [tenantId]);

  // Fallback bei loading/error: Layout-Höhe stabil halten, ohne Inhalt zu shifften.
  const firmaName =
    state.status === "ok" ? (state.data.tenant.firmaName ?? tenantId) : tenantId;
  const artikel = state.status === "ok" ? state.data.artikel.gesamt : "—";
  const konditionen = state.status === "ok" ? state.data.konditionen.gesamt : "—";
  const marge = state.status === "ok" ? `${state.data.tenant.margeProzent}%` : "—";

  return (
    <div className="ctx-tenant">
      <div className="ctx-tenant-left">
        <span className="tenant-dot" />
        <span className="tenant-name">{firmaName}</span>
        <span className="pill" style={{ padding: "2px 8px", fontSize: 10.5 }}>
          {tenantId.toUpperCase()}
        </span>
        <span className="tenant-sep" />
        <span className="tenant-stat">
          <span className="mono">{artikel}</span> Artikel
        </span>
        <span className="tenant-stat">
          <span className="mono">{konditionen}</span> Konditionen
        </span>
        <span className="tenant-stat">
          <span className="mono">{marge}</span> Marge
        </span>
      </div>
      <div className="ctx-tenant-right">
        <span className="uppercase-mini">Stand · {formatNow()}</span>
      </div>
    </div>
  );
}

function formatNow(): string {
  return new Date().toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}
